import { Client, type Room } from '@colyseus/sdk';
import type { IslandState } from '../server/IslandRoom';
import type { ActMsg, CollectMsg, CollectResult, MoveMsg, PublicPlayer, SelfState } from '../shared/types';

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';
export interface NetworkHandlers {
  status(status: ConnectionStatus, message?: string): void;
  players(players: PublicPlayer[], id: string): void;
  self(state: SelfState): void;
  result(result: CollectResult): void;
  actionError(message: string): void;
}
function serverUrl() {
  const configured = import.meta.env.VITE_SERVER_URL;
  if (configured) return configured;
  const url = new URL(location.href);
  if (import.meta.env.DEV) url.port = '2567';
  return url.origin;
}
export class IslandConnection {
  private room?: Room<IslandState>;
  private closed = false;
  private connected = false;
  private latestMove?: MoveMsg;
  private moveTimer?: ReturnType<typeof setInterval>;
  private syncTimer?: ReturnType<typeof setInterval>;
  constructor(private handlers: NetworkHandlers) {}
  async join(nickname: string) {
    this.handlers.status('connecting');
    try {
      const room = await new Client(serverUrl()).joinOrCreate<IslandState>('island', { nickname });
      if (this.closed) { await room.leave(); return; }
      this.room = room;
      room.reconnection.minUptime = 0;
      room.reconnection.minDelay = 500;
      room.reconnection.maxDelay = 2000;
      room.reconnection.maxRetries = 30;
      room.onMessage('self_state', (self: SelfState) => {
        if (this.closed) return;
        if (this.syncTimer) { clearInterval(this.syncTimer); this.syncTimer = undefined; }
        this.connected = true; this.handlers.self(self); this.handlers.status('connected');
      });
      room.onMessage('collect_result', (result: CollectResult) => { if (!this.closed) this.handlers.result(result); });
      room.onMessage('action_error', (message: string) => { if (!this.closed) this.handlers.actionError(message); });
      room.onStateChange(state => {
        if (this.closed) return;
        const players: PublicPlayer[] = [];
        state.players.forEach(p => players.push({ id: p.id, nickname: p.nickname, skin: p.skin, x: p.x, z: p.z, rotY: p.rotY, action: p.action, connected: p.connected }));
        this.handlers.players(players, room.sessionId);
      });
      room.onDrop(() => {
        if (this.closed) return;
        this.connected = false; this.latestMove = undefined; clearInterval(this.syncTimer); this.syncTimer = undefined; this.handlers.status('reconnecting');
      });
      room.onReconnect(() => { if (!this.closed) { this.connected = false; this.requestSelf(); } });
      room.onLeave(() => {
        this.connected = false; clearInterval(this.syncTimer); clearInterval(this.moveTimer);
        if (!this.closed) this.handlers.status('disconnected', '연결이 종료되었어요. 다시 입장하면 새 주머니로 시작해요.');
      });
      room.onError((_code, message) => { if (!this.closed) this.handlers.actionError(message || '연결 상태를 확인하고 있어요.'); });
      this.moveTimer = setInterval(() => {
        if (this.connected && this.latestMove) { room.send('move', this.latestMove); this.latestMove = undefined; }
      }, 1000 / 12);
      this.requestSelf();
    } catch {
      if (!this.closed) this.handlers.status('error', '섬에 연결하지 못했어요. 잠시 후 다시 입장해 주세요.');
    }
  }
  private requestSelf() {
    if (this.syncTimer) clearInterval(this.syncTimer);
    this.room?.send('sync_self');
    this.syncTimer = setInterval(() => this.room?.send('sync_self'), 1000);
  }
  move(m: MoveMsg) { if (this.connected) this.latestMove = m; }
  act(m: ActMsg) { if (this.connected) this.room?.send('act', m); }
  collect(m: CollectMsg) { if (this.connected) this.room?.send('collect', m); }
  sync() { if (this.connected) this.room?.send('sync_self'); }
  close() {
    this.closed = true; this.connected = false;
    clearInterval(this.moveTimer); clearInterval(this.syncTimer);
    void this.room?.leave();
  }
}
